import { apiRequest, jsonBody } from "./client";
import type {
  AccessCodeStatus, AccountDeletionReceipt, Allocation, AuthResponse, CategoriesResponse, FavoritePage,
  GameDetail, GamesResponse, HistoryPage, HomeResponse, InboxPage, LaunchMapping,
  MembershipCatalog, MembershipSnapshot, PaymentEligibility, PlayerPage, Review, ReviewPage,
  DailyGameTimeResult, SafetyPage, StarparksSession, User, WebAllocation,
} from "./types";

export const catalogApi = {
  home: (collectionLimit = 12) => apiRequest<HomeResponse>("/v1/catalog/home", { query: { collectionLimit } }),
  categories: () => apiRequest<CategoriesResponse>("/v1/catalog/categories"),
  games: (query: { q?: string; collection?: string; category?: string; availability?: string; page?: number; size?: number; sort?: string } = {}) =>
    apiRequest<GamesResponse>("/v1/catalog/games", { query }),
  library: (collection: "rankings" | "premium", query: { category?: string; page?: number; size?: number; sort?: string } = {}) =>
    apiRequest<GamesResponse>(`/v1/catalog/library/${collection}`, { query }),
  detail: (id: string) => apiRequest<GameDetail>(`/v1/catalog/games/${encodeURIComponent(id)}`),
  launchMapping: (id: string) => apiRequest<LaunchMapping>(`/v1/catalog/games/${encodeURIComponent(id)}/launch-mapping`, { auth: true }),
};

export const authApi = {
  guest: (guestCredential: string, deviceId: string) => apiRequest<AuthResponse>("/v1/auth/guest", {
    method: "POST", ...jsonBody({ guestCredential, deviceId }),
  }),
  provider: (provider: string, providerCredential: string, deviceId: string, nonce?: string) =>
    apiRequest<AuthResponse>(`/v1/auth/${encodeURIComponent(provider)}`, {
      method: "POST", ...jsonBody({ providerCredential, deviceId, nonce }),
    }),
  me: () => apiRequest<User>("/v1/me", { auth: true, query: { refreshEntitlement: true } }),
  updateNickname: (nickname: string) => apiRequest<User>("/v1/me/profile", {
    auth: true, method: "PATCH", ...jsonBody({ nickname }),
  }),
  bindIdentity: (provider: string, providerCredential: string, nonce?: string) =>
    apiRequest<User>(`/v1/me/identities/${encodeURIComponent(provider)}/bind`, {
      auth: true, method: "POST", ...jsonBody({ providerCredential, nonce }),
    }),
  paymentEligibility: () => apiRequest<PaymentEligibility>("/v1/me/payment-eligibility", { auth: true }),
  accessCodeStatus: () => apiRequest<AccessCodeStatus>("/v1/me/access-code", { auth: true }),
  redeemAccessCode: (code: string) => apiRequest<AccessCodeStatus>("/v1/me/access-code", {
    auth: true, method: "POST", ...jsonBody({ code: code.trim() }),
  }),
  deleteAccount: (receiptToken: string) => apiRequest<AccountDeletionReceipt>("/v1/me/account-deletion", {
    auth: true, method: "POST", ...jsonBody({ receiptToken, confirmation: "DELETE" }),
  }),
  logout: () => apiRequest<void>("/v1/auth/logout", { auth: true, method: "POST", ...jsonBody({}) }),
};

export const libraryApi = {
  favorites: (page = 0) => apiRequest<FavoritePage>("/v1/me/favorites", { auth: true, query: { limit: 100, page } }),
  favoriteState: (starparksId: number) => apiRequest<{ favorite: boolean }>(`/v1/me/favorites/by-starparks/${starparksId}`, { auth: true }),
  setFavorite: (starparksId: number, favorite: boolean) => apiRequest<void>(`/v1/me/favorites/by-starparks/${starparksId}`, {
    auth: true, method: favorite ? "PUT" : "DELETE",
  }),
  history: () => apiRequest<HistoryPage>("/v1/me/play-history", { auth: true, query: { limit: 100 } }),
  recordPlay: (gameId: string, seconds: number, sessionId?: string) => apiRequest<unknown>(`/v1/me/play-history/${gameId}`, {
    auth: true, method: "PUT", ...jsonBody({ playSeconds: seconds, sessionId, eventId: crypto.randomUUID() }),
  }),
  heartbeat: (sessionId: string, starparksGameId: number, sequence: number, activeSeconds: number, ended: boolean) =>
    apiRequest<unknown>(`/v1/me/play-sessions/${encodeURIComponent(sessionId)}`, {
      auth: true, method: "PUT", ...jsonBody({ starparksGameId, sequence, activeSeconds, ended }),
    }),
};

export const reviewApi = {
  list: (gameId: string, page = 0, sort = "popular") => apiRequest<ReviewPage>(`/v1/catalog/games/${gameId}/reviews`, {
    auth: "optional", query: { page, size: 20, sort },
  }),
  mine: (gameId: string) => apiRequest<Review | undefined>(`/v1/catalog/games/${gameId}/reviews/me`, { auth: true }),
  context: (gameId: string) => apiRequest<{ hasPlayed: boolean; review?: Review | null }>(`/v1/catalog/games/${gameId}/reviews/context`, { auth: true }),
  save: (gameId: string, body: { recommended: boolean; body: string; communityRulesVersion: string }) => apiRequest<Review>(`/v1/catalog/games/${gameId}/reviews/me`, {
    auth: true, method: "PUT", ...jsonBody(body),
  }),
  remove: (gameId: string) => apiRequest<void>(`/v1/catalog/games/${gameId}/reviews/me`, { auth: true, method: "DELETE" }),
  vote: (reviewId: string, vote: "LIKE" | "DISLIKE" | "NONE") => vote === "NONE"
    ? apiRequest<{ likeCount: number; dislikeCount: number; vote: string }>(`/v1/catalog/reviews/${reviewId}/vote`, { auth: true, method: "DELETE" })
    : apiRequest<{ likeCount: number; dislikeCount: number; vote: string }>(`/v1/catalog/reviews/${reviewId}/vote`, { auth: true, method: "PUT", ...jsonBody({ vote }) }),
};

export const socialApi = {
  inbox: (kind: "SYSTEM" | "SOCIAL" = "SYSTEM", page = 0) => apiRequest<InboxPage>("/v1/me/inbox", { auth: true, query: { kind, page, size: 50 } }),
  markRead: (id: string) => apiRequest<void>(`/v1/me/inbox/${id}/read`, { auth: true, method: "PUT" }),
  markAllRead: () => apiRequest<void>("/v1/me/inbox/read-all", { auth: true, method: "PUT" }),
  friends: (relation: "FOLLOWING" | "FOLLOWERS" | "MUTUAL" = "MUTUAL", page = 0) =>
    apiRequest<PlayerPage>("/v1/me/friends", { auth: true, query: { relation, page, size: 50 } }),
  searchPlayers: (q: string, page = 0) => apiRequest<PlayerPage>("/v1/me/friends/search", {
    auth: true, query: { q: q.trim(), page, size: 50 },
  }),
  setFollowing: (userId: string, following: boolean) => apiRequest<void>(`/v1/me/following/${encodeURIComponent(userId)}`, {
    auth: true, method: following ? "PUT" : "DELETE",
  }),
};

export const communityApi = {
  reportReview: (reviewId: string, reason: string, details = "") => apiRequest<void>(`/v1/community/reviews/${encodeURIComponent(reviewId)}/reports`, {
    auth: true, method: "POST", ...jsonBody({ target: "REVIEW", reason, details: details.trim() }),
  }),
  hideReview: (reviewId: string, hidden: boolean) => apiRequest<void>(`/v1/community/hidden-reviews/${encodeURIComponent(reviewId)}`, {
    auth: true, method: hidden ? "PUT" : "DELETE",
  }),
  hiddenReviews: (page = 0) => apiRequest<SafetyPage>("/v1/community/hidden-reviews", { auth: true, query: { page } }),
  blockUser: (userId: string, blocked: boolean) => apiRequest<void>(`/v1/community/blocked-users/${encodeURIComponent(userId)}`, {
    auth: true, method: blocked ? "PUT" : "DELETE",
  }),
  blockedUsers: (page = 0) => apiRequest<SafetyPage>("/v1/community/blocked-users", { auth: true, query: { page } }),
};

export const membershipApi = {
  catalog: () => apiRequest<MembershipCatalog>("/v1/membership/catalog", { auth: true }),
  mine: () => apiRequest<MembershipSnapshot>("/v1/me/membership", { auth: true }),
};

export const cloudApi = {
  time: () => apiRequest<{ balanceMinutes: number }>("/v1/cloud/starparks/time", { auth: true }),
  session: (catalogGameId: string, deviceId: string, country: string) => apiRequest<StarparksSession>("/v1/cloud/starparks/session", {
    auth: true, method: "POST", ...jsonBody({ catalogGameId, deviceId, country }),
  }),
  webAllocation: async (catalogGameId: string, deviceId: string, country: string, gameMode: number, onStaleAllocation?: (allocation: WebAllocation) => Promise<void>): Promise<WebAllocation> => {
    type RawAllocation = {
      gameId?: number; gameProcessId?: number; gpIp: string; tcpPort: number; udpPort?: number;
      gameMode: number; token: string; gsId?: number; gsmId?: number; gameExtraId: number;
      sessionId?: string; bridgeTicket: string; bridgeTicketExpiresAt: string;
    };
    const mapAllocation = (raw: RawAllocation): WebAllocation => {
      const allocation: Allocation = {
        game_id: raw.gameId, game_process_id: raw.gameProcessId, gp_ip: raw.gpIp,
        tcp_port: raw.tcpPort, udp_port: raw.udpPort, game_mode: raw.gameMode,
        token: raw.token, gs_id: raw.gsId, gsm_id: raw.gsmId,
        game_extra_id: raw.gameExtraId, session_id: raw.sessionId,
      };
      return { ...allocation, bridgeTicket: raw.bridgeTicket, bridgeTicketExpiresAt: raw.bridgeTicketExpiresAt };
    };
    const raw = await apiRequest<RawAllocation>("/v1/cloud/starparks/web-allocation", {
      auth: true, method: "POST", ...jsonBody({ catalogGameId, deviceId, country, gameMode }),
      onDiscardedResponse: onStaleAllocation ? async (body) => {
        const discarded = body as RawAllocation | null;
        if (discarded?.bridgeTicket && discarded?.gpIp && discarded?.token) await onStaleAllocation(mapAllocation(discarded));
      } : undefined,
    });
    return mapAllocation(raw);
  },
  dailyReward: (deviceId: string, country: string) => apiRequest<DailyGameTimeResult>("/v1/cloud/starparks/daily-reward", {
    auth: true, method: "POST", ...jsonBody({ deviceId, country }),
  }),
};
